import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import _isEqualWith from 'lodash/isEqualWith';
import { KEY_VALUE_LAYOUT, KeyConfig } from 'commons-ui';
import { GAME_NAME_OPTIONS, GAME_NAMES } from 'pages/constants';
import useService from './useService';
import { VIEW_FIELD_KEYS } from './constants';
import { mttUserGameColumnConfigs } from './mttUserGameListConfig';

export const getKeyConfig = ({
	ltid,
	mttid,
	cgrid,
	gameName,
	playingAttempt,
	gameType,
	pwfInviteId
}: {
	ltid?: number | null;
	mttid?: number | null;
	cgrid?: number | null;
	gameName?: string | null;
	playingAttempt?: number | null;
	gameType?: string | null;
	pwfInviteId?: string | null;
}) => {
	const {
		analyzeTournament,
		tournamentStartTime,
		forceUpdateChild,
		setForceUpdate,
		getGameplayTimelineData,
		getRoundInfo,
		showLoader,
		tableId,
		gameEndTime,
		gameStartTime,
		gameUid,
		registeredOn,
		tournamentStartTimeInMS,
		tournamentEndTimeInMS,
		registeredOnInMS,
		entryFee,
		transactionId,
		winnings,
		score,
		resultData,
		inning
	} = useService({
		ltid,
		mttid,
		cgrid,
		gameName,
		playingAttempt,
		gameType,
		pwfInviteId
	});

	const turnGlobalForceUpdateOn = () => {
		setForceUpdate(true);
	};

	const keyConfig: KeyConfig = {
		childConfig: [
			...(_isNil(mttid)
				? [
						{
							name: VIEW_FIELD_KEYS.TABLE_ID,
							label: 'Table id'
						}
				  ]
				: []),
			{
				name: VIEW_FIELD_KEYS.ENTRY_FEE,
				label: 'Entry fee (₹)'
			},
			{
				name: VIEW_FIELD_KEYS.TOURNAMENT_START_TIME,
				label: 'Tournament start time'
			},
			{
				name: VIEW_FIELD_KEYS.TOURNAMENT_END_TIME,
				label: 'Tournament end time'
			},
			{
				name: VIEW_FIELD_KEYS.TOTAL_REGISTRATIONS,
				label: 'Total registrations'
			},
			{
				name: VIEW_FIELD_KEYS.PRIZE_POT,
				label: 'Prize pot (₹)'
			},
			{
				name: VIEW_FIELD_KEYS.MINIMUM_PLAYERS,
				label: 'Minimum players'
			},
			{
				name: VIEW_FIELD_KEYS.MAXIMUM_PLAYERS,
				label: 'Maximum players'
			},
			{
				name: VIEW_FIELD_KEYS.TYPE,
				label: 'Type'
			},
			{
				name: VIEW_FIELD_KEYS.PAYOUT_DISTRIBUTION,
				label: 'Payout distribution',
				override: {
					format: 'json',
					isPayoutDistribution: true
				}
			},
			{
				name: VIEW_FIELD_KEYS.TOURNAMENT_STATUS,
				label: 'Tournament status'
			},
			{
				name: VIEW_FIELD_KEYS.REFUNDED,
				label: 'Refunded status'
			},
			{
				name: VIEW_FIELD_KEYS.REFUNDED_ON,
				label: 'Refunded on'
			},
			{
				name: VIEW_FIELD_KEYS.USER_GAME_DETAILS,
				label: 'User Game Details',
				initiallyCollapsed: true,
				...(_isNil(mttid)
					? {
							childConfig: [
								{
									name: VIEW_FIELD_KEYS.REGISTERED_ON,
									label: 'Registered on'
								},
								{
									name: VIEW_FIELD_KEYS.JOINED_ON,
									label: 'Joined on'
								},
								...(_isNil(cgrid)
									? [
											{
												name: VIEW_FIELD_KEYS.GAME_START_TIME,
												label: 'Game start time'
											},
											{
												name: VIEW_FIELD_KEYS.GAME_END_TIME,
												label: 'Game end time'
											}
									  ]
									: []),
								...(_isEmpty(gameName) ||
								(!_isEmpty(gameName) && !_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]))
									? [
											{
												name: VIEW_FIELD_KEYS.FINAL_TOKEN_POSITIONS,
												label: 'Final token positions',
												override: {
													format: 'json',
													jsonKeyLabelMap: { 0: 'token-1', 1: 'token-2', 2: 'token-3', 3: 'token-4' }
												}
											}
									  ]
									: [
											{
												name: VIEW_FIELD_KEYS.GAME_BOARD_TOKEN_POSITIONS,
												label: 'Game board token positions',
												override: {
													format: 'json',
													jsonKeyLabelMap: { 0: 'Player-1', 1: 'Player-2', 2: 'Player-3', 3: 'Player-4' }
												}
											}
									  ])
							]
					  }
					: {
							override: {
								format: 'datalist',
								datalistProps: {
									columnConfigs: mttUserGameColumnConfigs,
									fetchData: getRoundInfo,
									height: '200px',
									filterable: false,
									searchable: false,
									disablePagination: true,
									globalForceUpdate: forceUpdateChild,
									showFooterMessage: false
								}
							}
					  })
			},
			{
				name: VIEW_FIELD_KEYS.PAYMENT,
				label: 'Payment',
				initiallyCollapsed: true,
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.USER_PAYMENT_TRANSACTION_ID,
						label: 'User payment transaction id'
					},
					{
						name: VIEW_FIELD_KEYS.BONUSMONEY,
						label: 'Bonus money (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.DEPOSIT,
						label: 'Deposit (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.WINNINGS,
						label: 'Winnings (₹)'
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.FINAL_RESULT_FOR_USER,
				label: 'Final Result for User',
				initiallyCollapsed: true,
				childConfig: [
					...(!_isNil(mttid) || (!_isEmpty(gameName) && _isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]))
						? [
								{
									name: VIEW_FIELD_KEYS.WINNING_AMOUNT,
									label: 'Winnings (₹)'
								}
						  ]
						: [
								{
									name: VIEW_FIELD_KEYS.WINNING_AMOUNT,
									label: 'Winning Amount (₹)'
								},
								{
									name: VIEW_FIELD_KEYS.WINNINGS,
									label: 'Winnings (₹)'
								},
								{
									name: VIEW_FIELD_KEYS.BONUSMONEY,
									label: 'Bonus money (₹)'
								},
								{
									name: VIEW_FIELD_KEYS.DEPOSIT,
									label: 'Deposit (₹)'
								}
						  ]),

					...(_isNil(mttid)
						? [
								{
									name: VIEW_FIELD_KEYS.SCORE,
									label: 'Score'
								}
						  ]
						: []),
					{
						name: VIEW_FIELD_KEYS.RANK,
						label: 'Rank'
					}
				]
			}
		],
		fetchData: analyzeTournament,
		name: VIEW_FIELD_KEYS.TOURNAMENT_DETAILS,
		label: `Tournament(${ltid || mttid || pwfInviteId}) details`,
		initiallyCollapsed: false
	};

	return {
		keyConfig,
		tournamentStartTime,
		keyValueLayout: KEY_VALUE_LAYOUT.VERTICAL,
		forceUpdateChild,
		turnGlobalForceUpdateOn,
		getGameplayTimelineData,
		showLoader,
		tableId,
		gameEndTime,
		gameStartTime,
		gameUid,
		registeredOn,
		tournamentStartTimeInMS,
		tournamentEndTimeInMS,
		registeredOnInMS,
		entryFee,
		transactionId,
		winnings,
		score,
		resultData,
		inning
	};
};
